// src/pages/kiosk/KioskApp.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import NiceSelect from "../../components/NiceSelect";
import "./kiosk.css";

const LS_KIOSK_TOKEN = "kioskToken";

/* ---------------------- centered modal-style toast ---------------------- */
function Toast({ msg, kind = "info", onClose }) {
  if (!msg) return null;
  return (
    <div className="toast-overlay" role="dialog" aria-modal="true">
      <div className={`toast-modal toast--${kind}`}>
        <button
          className="toast__close toast__close--abs"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ×
        </button>
        <div className="toast__content">
          <div className="toast__msg">{msg}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- keypad ------------------------- */
function Keypad({ value, onChange, onEnter, disabled, className = "" }) {
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "←", "0", "✓"];
  const press = (k) => {
    if (disabled) return;
    if (k === "←") onChange(String(value || "").slice(0, -1));
    else if (k === "✓") onEnter?.();
    else onChange(String(value || "") + k);
  };
  return (
    <div
      className={`keypad keypad--modern ${className}`}
      aria-label="Phone keypad"
    >
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          className="keypad__btn"
          disabled={disabled}
          onClick={() => press(k)}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

/* --------------------- helpers: format check-in --------------------- */
function buildWhereString(last_checkin) {
  if (!last_checkin) return null;

  const loc =
    last_checkin.location ||
    last_checkin.location_name ||
    last_checkin.event_location ||
    last_checkin.venue ||
    last_checkin.place ||
    last_checkin.event?.location ||
    null;

  const title = last_checkin.event_title || last_checkin.event?.title || null;

  const parts = [];
  if (loc && typeof loc === "string") parts.push(loc.trim());
  if (title && typeof title === "string") parts.push(title.trim());
  if (!parts.length) return null;
  return parts.join(" • ");
}

/* ----------------------- component ------------------------ */
export default function KioskApp() {
  const [booting, setBooting] = useState(true);
  const [kioskCode, setKioskCode] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [stage, setStage] = useState("welcome");
  const [mode, setMode] = useState("name");

  const [q, setQ] = useState("");
  const [families, setFamilies] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pending, setPending] = useState(false);

  const [searchMeta, setSearchMeta] = useState({ status: "idle", count: 0 });
  const querySeq = useRef(0);

  const [selectedSingle, setSelectedSingle] = useState(null);
  const inputRef = useRef(null);

  /* ---------------------- Toast Logic ---------------------- */
  const [toast, setToast] = useState({ msg: "", kind: "info" });
  const toastTimerRef = useRef(null);

  const showToast = (msg, kind = "info") => {
    setToast({ msg, kind });

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }

    // auto close success after 1 sec
    if (kind === "success") {
      toastTimerRef.current = setTimeout(() => {
        setToast({ msg: "", kind: "info" });
      }, 1000);
    }
  };

  const clearToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = null;
    setToast({ msg: "", kind: "info" });
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ---------------------- Boot Kiosk Session ---------------------- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/kiosk/session/start", {
          kiosk_code: kioskCode || undefined,
        });

        if (data?.token) localStorage.setItem(LS_KIOSK_TOKEN, data.token);
        setEvents(data?.events || []);
        setStage("event");
      } catch {
        setStage("welcome");
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const chosenEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)),
    [events, eventId]
  );

  /* ---------------------- Reset Search State ---------------------- */
  const resetSearch = () => {
    setQ("");
    setFamilies([]);
    setSelectedIds(new Set());
    setSelectedSingle(null);
    setSearchMeta({ status: "idle", count: 0 });

    setTimeout(() => inputRef.current?.focus(), 50);
  };

  /* ---------------------- Toggle Functions (FIX) ---------------------- */
  const toggleMember = (m) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(m.id)) next.delete(m.id);
      else next.add(m.id);
      return next;
    });
  };

  const toggleFamily = (fam) => {
    const ids = fam.members.map((m) => m.id);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));

      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));

      return next;
    });
  };

  /* ------------------------ Search Helpers ------------------------ */
  const fetchSearch = async (s, { silent = false } = {}) => {
    const seq = ++querySeq.current;
    const qParam = mode === "phone" ? String(s).replace(/\D+/g, "") : s;

    if (!silent) {
      setPending(true);
      setSearchMeta((m) => ({ ...m, status: "searching" }));
    } else {
      setSearchMeta({ status: "searching", count: 0 });
    }

    try {
      const { data } = await api.get("/kiosk/search", {
        params: { q: qParam, mode, event_id: eventId || undefined },
      });

      if (seq !== querySeq.current) return;

      const rows = data || [];
      setFamilies(rows);
      setSearchMeta({
        status: "done",
        count: rows.reduce((n, f) => n + (f?.members?.length || 0), 0),
      });
    } catch {
      if (!silent) showToast("Search failed. Please try again.", "error");
      setFamilies([]);
      setSearchMeta({ status: "error", count: 0 });
    } finally {
      if (!silent) setPending(false);
    }
  };

  const doSearch = async () => {
    const raw = q.trim();
    if (!raw) {
      setFamilies([]);
      setSearchMeta({ status: "idle", count: 0 });
      return;
    }
    await fetchSearch(raw);
  };

  /* ------------------------ Debounced Typeahead ------------------------ */
  useEffect(() => {
    const raw = q.trim();
    const phoneDigits = raw.replace(/\D+/g, "");
    const qForServer = mode === "phone" ? phoneDigits : raw;
    const meetsThreshold =
      mode === "phone" ? phoneDigits.length >= 3 : raw.length >= 2;

    if (!meetsThreshold) {
      setFamilies([]);
      setSearchMeta({ status: "idle", count: 0 });
      return;
    }

    const t = setTimeout(() => fetchSearch(qForServer, { silent: true }), 350);
    return () => clearTimeout(t);
  }, [q, mode, eventId]);

  /* ------------------------ Bulk Check-In ------------------------ */
  const selectedMembers = useMemo(() => {
    const map = new Map();
    families.forEach((f) => f.members.forEach((m) => map.set(m.id, m)));
    return [...selectedIds].map((id) => map.get(id)).filter(Boolean);
  }, [selectedIds, families]);

  const allSelectedAreIn =
    selectedMembers.length > 0 &&
    selectedMembers.every((m) => m?.last_checkin?.checked_in);

  const goToEventAfterSuccess = () => {
    setTimeout(() => {
      setEventId("");
      setStage("event");
      resetSearch();
    }, 1000);
  };

  const doBulkCheckin = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !eventId) return;

    setPending(true);
    clearToast();

    try {
      const { data } = await api.post("/kiosk/checkins/bulk", {
        event_id: eventId,
        entity_ids: ids,
      });

      const inserted = data?.inserted ?? 0;
      const skipped = data?.skipped ?? Math.max(0, ids.length - inserted);

      showToast(
        `✅ Checked in ${inserted} member(s)${
          skipped ? ` • Skipped ${skipped}` : ""
        }.`,
        "success"
      );

      resetSearch();
      goToEventAfterSuccess();
    } catch (e) {
      const reason =
        e?.response?.data?.message ||
        e?.message ||
        "Check-in failed. Please try again.";
      showToast(`❌ ${reason}`, "error");
    } finally {
      setPending(false);
    }
  };

  /* ------------------------ Bulk Check-Out ------------------------ */
  const doBulkCheckout = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !eventId) return;

    setPending(true);
    clearToast();

    try {
      const { data } = await api.post("/kiosk/checkouts/bulk", {
        event_id: eventId,
        entity_ids: ids,
      });

      showToast(`✅ Checked out ${data?.affected ?? 0} member(s).`, "success");

      resetSearch();
      goToEventAfterSuccess();
    } catch (e) {
      const reason =
        e?.response?.data?.message ||
        e?.message ||
        "Check-out failed. Please try again.";
      showToast(`❌ ${reason}`, "error");
    } finally {
      setPending(false);
    }
  };

  /* ------------------------ Render Families (FIXED SCOPE) ------------------------ */
  const renderFamilies = () => (
    <div className="families">
      {families.map((fam, idx) => {
        const ids = fam.members.map((m) => m.id);
        const allSelected = ids.every((id) => selectedIds.has(id));
        const someSelected =
          !allSelected && ids.some((id) => selectedIds.has(id));

        const headerLabel =
          fam.family_id &&
          fam.label &&
          fam.label.toLowerCase() !== "no family record"
            ? fam.label
            : "Results";

        return (
          <div className="family" key={idx}>
            <div className="family-head">
              {mode === "phone" && (
                <label
                  className={`chk ${
                    allSelected ? "on" : someSelected ? "partial" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => toggleFamily(fam)}
                  />
                  <span />
                </label>
              )}

              <div className="family-title">{headerLabel}</div>

              <div className="spacer" />

              <button className="pill" onClick={() => toggleFamily(fam)}>
                {allSelected ? "Unselect All" : "Select All"}
              </button>
            </div>

            <div className="family-members">
              {fam.members.map((m) => {
                const isSel = selectedIds.has(m.id);
                const role =
                  m.type?.toLowerCase() === "elder" ? "Elder" : "Member";
                const time = m.last_checkin?.time_display;
                const whereText = buildWhereString(m.last_checkin);
                const isIn = !!m.last_checkin?.checked_in;

                return (
                  <button
                    type="button"
                    key={m.id}
                    className={`member ${isSel ? "selected" : ""}`}
                    onClick={() => toggleMember(m)}
                    onDoubleClick={() => {
                      setSelectedSingle(m);
                      setStage("confirm");
                    }}
                  >
                    <label className="chk member-chk">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleMember(m)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span />
                    </label>

                    <div className="member-container">
                      <div className="name">
                        {m.first_name} {m.last_name}
                        {isIn && (
                          <span className="k-status k-status--in">
                            Checked-in
                          </span>
                        )}
                      </div>

                      <div className="meta">
                        {role} • {m.phone || "—"}
                        {time && (
                          <>
                            &nbsp;•&nbsp;
                            <span className="chip chip--time">🕒 {time}</span>
                          </>
                        )}
                        {whereText && (
                          <>
                            &nbsp;•&nbsp;
                            <span className="chip chip--place">
                              📍 {whereText}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  /* --------------------------- STAGES --------------------------- */
  if (booting) {
    return (
      <div className="kiosk-root kiosk--light">
        <div className="kiosk-card">Starting kiosk…</div>
      </div>
    );
  }

  /* --------------------------- WELCOME --------------------------- */
  if (stage === "welcome") {
    return (
      <div className="kiosk-root kiosk--light">
        <div className="kiosk-card">
          <h1>Welcome</h1>
          <p>
            Enter kiosk code (optional) to bind to a device, or press Start.
          </p>

          <input
            value={kioskCode}
            onChange={(e) => setKioskCode(e.target.value)}
            className="input"
            placeholder="Kiosk code (optional)"
          />

          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                const { data } = await api.post("/kiosk/session/start", {
                  kiosk_code: kioskCode || undefined,
                });

                if (data?.token) {
                  localStorage.setItem(LS_KIOSK_TOKEN, data.token);
                }

                setEvents(data?.events || []);
                setStage("event");
              } catch (e) {
                showToast(
                  e?.response?.data?.message || "Invalid kiosk code",
                  "error"
                );
              }
            }}
          >
            Start
          </button>
        </div>
      </div>
    );
  }

  /* --------------------------- EVENT SELECT --------------------------- */
  if (stage === "event") {
    return (
      <div className="kiosk-root kiosk--light">
        <div className="kiosk-card kiosk-card--wide slim">
          <div className="heading">
            <h1>Select Event</h1>
            <p className="muted">
              Choose the service or gathering to check into
            </p>
          </div>

          <div className="select-wrap">
            <NiceSelect
              options={events}
              value={eventId}
              onChange={(v) => setEventId(v)}
              getLabel={(ev) =>
                `${ev.title} • ${new Date(ev.event_date).toLocaleDateString()}`
              }
              getValue={(ev) => ev.id}
              placeholder="— Choose an event —"
            />
          </div>

          <div className="k-actions">
            <button
              className="btn btn-primary btn-lg"
              disabled={!eventId}
              onClick={() => {
                setStage("search");
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* --------------------------- SEARCH SCREEN --------------------------- */
  if (stage === "search") {
    const countSelected = selectedIds.size;

    return (
      <div className="kiosk-root kiosk--light">
        <div className="kiosk-card kiosk-card--wide slim">
          <h1>Self Check-In Mode</h1>
          <p className="muted">
            Event: <b>{chosenEvent?.title}</b>
          </p>

          {/* Mode Switch */}
          <div className="search-modes">
            <button
              type="button"
              className={mode === "name" ? "tab active" : "tab"}
              onClick={() => {
                setMode("name");
                resetSearch();
              }}
            >
              Search by Name
            </button>

            <button
              type="button"
              className={mode === "phone" ? "tab active" : "tab"}
              onClick={() => {
                setMode("phone");
                resetSearch();
              }}
            >
              Search by Phone
            </button>
          </div>

          {/* Search Bar & Button */}
          <div className="kiosk-row">
            <div>
              <div className="input-wrap">
                <input
                  ref={inputRef}
                  className="input input--xl"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={
                    mode === "phone"
                      ? "Enter phone digits (e.g., 8042293809)"
                      : "Type first/last name"
                  }
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                />

                {q && (
                  <button
                    type="button"
                    className="input-clear"
                    aria-label="Clear"
                    onClick={() => setQ("")}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                {searchMeta.status === "searching"
                  ? "Searching…"
                  : searchMeta.status === "done"
                  ? `${searchMeta.count} result${
                      searchMeta.count === 1 ? "" : "s"
                    }`
                  : null}
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={doSearch}
              disabled={pending}
            >
              {pending ? "Searching…" : "Search"}
            </button>
          </div>

          {/* Phone Mode — keypad */}
          {mode === "phone" ? (
            <div className="phone-grid">
              <div className="phone-left">
                <div className="pc-title">phone</div>

                <div className="pc-number-display">
                  <div className="digits">{q.replace(/\D+/g, "") || "—"}</div>
                  <button
                    className="clear-btn"
                    type="button"
                    onClick={() => setQ("")}
                    aria-label="Clear"
                  >
                    ⌫
                  </button>
                </div>

                <Keypad
                  value={q.replace(/\D+/g, "")}
                  onChange={(v) => setQ(v)}
                  onEnter={doSearch}
                  disabled={pending}
                />

                <div className="phone-enter-hint">Press ✓ to search</div>
              </div>

              <div className="phone-right">
                <div className="pc-barcode-card">
                  <div className="pc-barcode-icon">▦</div>
                  <div className="pc-barcode-text">
                    Scan barcode (if available) or use the keypad.
                  </div>
                </div>

                <div className="phone-results">{renderFamilies()}</div>
              </div>
            </div>
          ) : (
            <div className="results-scroll">{renderFamilies()}</div>
          )}

          {/* Bottom Actions */}
          <div className="kiosk-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setStage("event")}
              title="Change Event"
            >
              Change Event
            </button>

            <div className="spacer" />

            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={!countSelected || pending}
              onClick={allSelectedAreIn ? doBulkCheckout : doBulkCheckin}
            >
              {pending
                ? allSelectedAreIn
                  ? "Checking out…"
                  : "Checking in…"
                : allSelectedAreIn
                ? `Check Out ${countSelected}`
                : `Check In ${countSelected}`}
            </button>
          </div>
        </div>

        <Toast msg={toast.msg} kind={toast.kind} onClose={clearToast} />
      </div>
    );
  }

  /* --------------------------- CONFIRM SCREEN --------------------------- */
  if (stage === "confirm") {
    return (
      <div className="kiosk-root kiosk--light">
        <div className="kiosk-card">
          <h1>Confirm Check-In</h1>
          <p className="muted">
            Event: <b>{chosenEvent?.title}</b>
          </p>

          <div className="kiosk-person">
            <div className="name">
              {selectedSingle?.first_name} {selectedSingle?.last_name}
            </div>

            <div className="meta">
              {selectedSingle?.type?.toLowerCase() === "elder"
                ? "Elder"
                : "Member"}{" "}
              • {selectedSingle?.phone || "—"}
            </div>
          </div>

          <div className="kiosk-actions">
            <button
              className="btn btn-ghost"
              onClick={() => setStage("search")}
            >
              Back
            </button>

            <button
              className="btn btn-primary"
              onClick={async () => {
                try {
                  await api.post("/kiosk/checkins", {
                    event_id: eventId,
                    entity_id: selectedSingle.id,
                  });

                  showToast("✅ Checked in successfully.", "success");

                  goToEventAfterSuccess();
                } catch (e) {
                  const reason =
                    e?.response?.data?.message ||
                    e?.message ||
                    "Check-in failed.";
                  showToast(`❌ ${reason}`, "error");
                }
              }}
            >
              Check In
            </button>
          </div>
        </div>

        <Toast msg={toast.msg} kind={toast.kind} onClose={clearToast} />
      </div>
    );
  }

  /* --------------------------- FALLBACK --------------------------- */
  return (
    <div className="kiosk-root kiosk--light">
      <div className="kiosk-card success">
        <h1>You're checked in ✅</h1>
      </div>
    </div>
  );
}
